// Tax calculation utilities
export const calculateTaxSummary = ({
  grossPay,
  frequency,
  state,
  filingStatus,
  dependents = 0,
  otherIncome = 0,
  deductions = 0,
  extraWithholding = 0
}) => {
  // Calculate annual gross income
  const annualGross = calculateAnnualAmount(grossPay, frequency);

  // Calculate taxable income
  const taxableIncome = annualGross - deductions;

  // Calculate federal tax
  const federalTax = calculateFederalTax(taxableIncome, filingStatus, dependents);

  // Calculate state tax
  const stateTax = calculateStateTax(taxableIncome, state);

  // Calculate FICA (Social Security + Medicare)
  const fica = calculateFICA(annualGross);

  // Calculate total tax
  const totalTax = federalTax + stateTax + fica;

  // Calculate net income
  const netIncome = annualGross - totalTax - extraWithholding;

  return {
    annualGross,
    taxableIncome,
    federalTax,
    stateTax,
    fica,
    totalTax,
    netIncome,
    effectiveTaxRate: (totalTax / annualGross) * 100
  };
};

// Helper function to calculate annual amount based on frequency
const calculateAnnualAmount = (amount, frequency) => {
  const multipliers = {
    weekly: 52,
    biweekly: 26,
    semimonthly: 24,
    monthly: 12,
    quarterly: 4,
    yearly: 1
  };

  return amount * multipliers[frequency];
};

// Calculate federal tax based on 2024 tax brackets
const calculateFederalTax = (taxableIncome, filingStatus, dependents) => {
  const standardDeductions = {
    single: 13850,
    married_separate: 13850,
    married_joint: 27700,
    head_of_household: 20800
  };

  const taxBrackets = {
    single: [
      { rate: 0.10, threshold: 0 },
      { rate: 0.12, threshold: 11600 },
      { rate: 0.22, threshold: 44725 },
      { rate: 0.24, threshold: 95375 },
      { rate: 0.32, threshold: 182100 },
      { rate: 0.35, threshold: 231250 },
      { rate: 0.37, threshold: 578125 }
    ],
    married_joint: [
      { rate: 0.10, threshold: 0 },
      { rate: 0.12, threshold: 23200 },
      { rate: 0.22, threshold: 89450 },
      { rate: 0.24, threshold: 190750 },
      { rate: 0.32, threshold: 364200 },
      { rate: 0.35, threshold: 462500 },
      { rate: 0.37, threshold: 693750 }
    ],
    head_of_household: [
      { rate: 0.10, threshold: 0 },
      { rate: 0.12, threshold: 16550 },
      { rate: 0.22, threshold: 63100 },
      { rate: 0.24, threshold: 100500 },
      { rate: 0.32, threshold: 191950 },
      { rate: 0.35, threshold: 243700 },
      { rate: 0.37, threshold: 609350 }
    ]
  };

  const brackets = taxBrackets[filingStatus] || taxBrackets.single;
  const standardDeduction = standardDeductions[filingStatus] || standardDeductions.single;
  const dependentCredit = dependents * 2000; // $2,000 per dependent

  let tax = 0;
  let remainingIncome = taxableIncome - standardDeduction;

  for (let i = brackets.length - 1; i >= 0; i--) {
    const bracket = brackets[i];
    if (remainingIncome > bracket.threshold) {
      tax += (remainingIncome - bracket.threshold) * bracket.rate;
      remainingIncome = bracket.threshold;
    }
  }

  return Math.max(0, tax - dependentCredit);
};

// Calculate state tax based on state-specific rates
const calculateStateTax = (taxableIncome, state) => {
  // State tax rates (simplified - should be expanded with actual state tax brackets)
  const stateTaxRates = {
    AL: 0.05,
    AK: 0.00,
    AZ: 0.025,
    AR: 0.055,
    CA: 0.093,
    CO: 0.045,
    CT: 0.065,
    DE: 0.066,
    FL: 0.00,
    GA: 0.055,
    HI: 0.11,
    ID: 0.06,
    IL: 0.0495,
    IN: 0.0323,
    IA: 0.06,
    KS: 0.057,
    KY: 0.05,
    LA: 0.06,
    ME: 0.075,
    MD: 0.0575,
    MA: 0.05,
    MI: 0.0425,
    MN: 0.0985,
    MS: 0.05,
    MO: 0.054,
    MT: 0.069,
    NE: 0.0684,
    NV: 0.00,
    NH: 0.00,
    NJ: 0.1075,
    NM: 0.049,
    NY: 0.0882,
    NC: 0.0525,
    ND: 0.029,
    OH: 0.0397,
    OK: 0.05,
    OR: 0.099,
    PA: 0.0307,
    RI: 0.0599,
    SC: 0.07,
    SD: 0.00,
    TN: 0.00,
    TX: 0.00,
    UT: 0.0495,
    VT: 0.0875,
    VA: 0.0575,
    WA: 0.00,
    WV: 0.065,
    WI: 0.0765,
    WY: 0.00,
    DC: 0.0875
  };

  const rate = stateTaxRates[state] || 0;
  return taxableIncome * rate;
};

// Calculate FICA (Social Security + Medicare)
const calculateFICA = (annualGross) => {
  const socialSecurityRate = 0.062; // 6.2% up to $168,600
  const medicareRate = 0.0145; // 1.45% no limit
  const socialSecurityCap = 168600;

  const socialSecurity = Math.min(annualGross, socialSecurityCap) * socialSecurityRate;
  const medicare = annualGross * medicareRate;

  return socialSecurity + medicare;
};

// Format tax summary for display
export const formatTaxSummary = (summary) => {
  return {
    annualGross: formatCurrency(summary.annualGross),
    taxableIncome: formatCurrency(summary.taxableIncome),
    federalTax: formatCurrency(summary.federalTax),
    stateTax: formatCurrency(summary.stateTax),
    fica: formatCurrency(summary.fica),
    totalTax: formatCurrency(summary.totalTax),
    netIncome: formatCurrency(summary.netIncome),
    effectiveTaxRate: summary.effectiveTaxRate.toFixed(1) + '%'
  };
}; 